import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import styled from '@emotion/styled';
import { forwardRef, useState, useRef, useEffect } from 'react';
import { getStyle } from './utils.es.js';

const TabItem = forwardRef((_a, ref) => {
    var { children, onClick } = _a, rest = __rest(_a, ["children", "onClick"]);
    return (jsx(Wrapper$1, Object.assign({ ref: ref, onClick: onClick }, rest, { children: children })));
});
const Wrapper$1 = styled.div `
  padding: 8px;
  width: 100%;
  height: 100%;
`;
TabItem.displayName = 'TabItem';

const TabItemHead = (_a) => {
    var { type, children, isActive, activeColor = '#258DFF', textColor, alignment } = _a, rest = __rest(_a, ["type", "children", "isActive", "activeColor", "textColor", "alignment"]);
    if (type === 'line')
        return (jsx(TabHeadLine, Object.assign({ alignment: alignment, className: isActive ? 'active' : '', textColor: textColor, activeColor: activeColor }, rest, { children: children })));
    if (type === 'card')
        return (jsx(TabHeadCard, Object.assign({ alignment: alignment, className: isActive ? 'active' : '', activeColor: activeColor, textColor: textColor }, rest, { children: children })));
    return (jsx(TabHeadButton, Object.assign({ className: isActive ? 'active' : '', activeColor: activeColor, textColor: textColor }, rest, { children: children })));
};
const baseCss = `
  transition: 0.2s;
  cursor: pointer;
  height: 32px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const TabHeadButton = styled.div(({ activeColor, textColor }) => `
border-radius: 999px;
background-color: transparent;
color: ${textColor || 'inherit'};
${baseCss}
&.active {
  background-color: ${activeColor};
  color: #fff;
}
`);
const TabHeadLine = styled.div(({ activeColor, alignment, textColor }) => `
  ${baseCss}
  ${alignment === 'justify' ? 'width: 100% !important;' : ''}
  color: ${textColor || 'inherit'};
  &::after {
    content: '';
    height: 2px;
    background-color: ${activeColor};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    opacity: 0;
    transition: all 0.3s ease;
  }
  &.active {
    &::after {
      opacity: 1;
    }
  }
`);
const TabHeadCard = styled.div(({ activeColor, alignment, textColor }) => `
  ${baseCss}
  ${alignment === 'justify' ? 'width: 100% !important;' : ''}
  color: ${textColor || 'inherit'};
  &.active {
    color: ${activeColor};
  }
`);
TabItemHead.displayName = 'TabItemHead';

const Tab = forwardRef((_a, ref) => {
    var _b;
    var { children, commonStyle, tabs = [], activeColor, textColor, type, customTabItemHead, alignment = 'left' } = _a, rest = __rest(_a, ["children", "commonStyle", "tabs", "activeColor", "textColor", "type", "customTabItemHead", "alignment"]);
    const [activeTab, setActiveTab] = useState(0);
    const tabHeadRef = useRef();
    const tabHead = tabHeadRef === null || tabHeadRef === void 0 ? void 0 : tabHeadRef.current;
    useEffect(() => {
        if (tabHead && tabHead.scroll) {
            const activeDom = tabHead.childNodes[activeTab];
            tabHead.scroll({
                left: activeDom.offsetLeft -
                    (tabHead.offsetWidth - activeDom.offsetWidth) / 2,
                behavior: 'smooth',
            });
        }
    }, [activeTab]);
    const Head = customTabItemHead || TabItemHead;
    return (jsxs(Wrapper, Object.assign({ ref: ref, commonStyle: commonStyle }, rest, { children: [!!tabs.length && (jsx(TabHeadWrapper, Object.assign({ alignment: alignment, ref: tabHeadRef }, { children: tabs.map((tab, index) => (jsx(Head, Object.assign({ id: `tab-${index}`, activeColor: activeColor, textColor: textColor, isActive: index === activeTab, alignment: alignment, onClick: () => {
                        setActiveTab(index);
                    }, type: type }, tab, { children: jsx("span", { dangerouslySetInnerHTML: { __html: tab.title } }) }), index))) }))), ((_b = children === null || children === void 0 ? void 0 : children.props) === null || _b === void 0 ? void 0 : _b.children[activeTab]) || (children === null || children === void 0 ? void 0 : children[activeTab])] })));
});
const getTabHeadAlignment = (alignment) => {
    if (alignment === 'justify')
        return 'space-around';
    return alignment;
};
const TabHeadWrapper = styled.div(({ alignment }) => `
  display: flex;
  overflow-x: scroll;
  white-space: nowrap;
  gap: 8px;
  justify-content: ${getTabHeadAlignment(alignment)};
  &::-webkit-scrollbar {
    display: none;
  }
`);
const Wrapper = styled.div(({ commonStyle }) => `
    width: 100%;
    position: relative;
    ${getStyle(commonStyle)};
`);
Tab.displayName = 'Tab';

export { TabItem as T, TabItemHead as a, Tab as b, TabHeadWrapper as c };
